import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { CheckCircle2, Clock3, FileText, NotebookPen, UserRound } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmationModal';
import { deleteTask, getTask, updateTaskDetail } from '../../hooks/tasks';
import { Task } from '../../utils/types';
import {
  formatTaskDate,
  formatTaskDateTime,
  getTaskUrgencyColor,
  getTaskUrgencyLabel,
  isTaskComplete,
  TaskField,
  TaskHero,
  TaskPrimaryButton,
  TaskPriorityBadge,
  TaskSecondaryButton,
  TaskSectionCard,
  TaskShell,
  TaskStatusBadge,
} from './components/taskUi';

export default function ViewTaskPage() {
  const { taskId, orgId } = useParams<{ taskId: string; orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchTask = async () => {
      if (!orgId || !taskId) {
        return;
      }

      try {
        const response = await getTask(orgId, taskId);
        if (response?.success) {
          setTask(response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [orgId, taskId]);

  const handleComplete = async () => {
    if (!orgId || !task) {
      return;
    }

    setProcessing(true);
    const response = await updateTaskDetail(orgId, task.id, 'status', 'completed');
    setProcessing(false);

    if (response?.success) {
      setTask(response.data);
      toast({
        title: 'Task completed',
        description: `${task.title} was marked complete.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Could not update task',
        description: response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    if (!orgId || !task) {
      return;
    }

    setProcessing(true);
    const response = await deleteTask(orgId, task.id);
    setProcessing(false);

    if (response?.success) {
      toast({
        title: 'Task deleted',
        description: `${task.title} was removed.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(`/organization/${orgId}/tasks/`);
    } else {
      toast({
        title: 'Could not delete task',
        description: response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <TaskShell compact>
        <TaskSectionCard title="Loading task" description="Please wait while the task record is loading.">
          <Button variant="ghost" isLoading loadingText="Loading" />
        </TaskSectionCard>
      </TaskShell>
    );
  }

  if (!task) {
    return (
      <TaskShell compact>
        <TaskSectionCard title="Task not found" description="The requested task could not be loaded.">
          <TaskSecondaryButton onClick={() => navigate(-1)}>Go back</TaskSecondaryButton>
        </TaskSectionCard>
      </TaskShell>
    );
  }

  return (
    <TaskShell compact>
      <TaskHero
        eyebrow="Task"
        title={task.title}
        description="Review ownership, schedule, and execution context from one focused task record."
        primaryAction={
          <TaskPrimaryButton leftIcon={<EditIcon />} onClick={() => navigate(`/organization/${orgId}/tasks/${task.id}/edit`)}>
            Edit task
          </TaskPrimaryButton>
        }
        secondaryAction={
          <TaskSecondaryButton onClick={() => navigate(`/organization/${orgId}/tasks/`)}>
            Back to tasks
          </TaskSecondaryButton>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <TaskSectionCard
            title="Task summary"
            description="Current delivery state and ownership at a glance."
            action={<TaskStatusBadge status={task.status} />}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <TaskField label="Priority" value={task.priority.replace('_', ' ')} />
              <TaskField label="Assignee" value={task.assigned_to_name || 'Unassigned'} />
              <TaskField label="Created by" value={task.created_by_name || 'Unknown'} />
              <TaskField label="Due" value={getTaskUrgencyLabel(task)} />
            </SimpleGrid>
          </TaskSectionCard>

          <TaskSectionCard
            title="Description"
            description="The main working brief for this task."
          >
            <HStack align="flex-start" spacing={3}>
              <Icon as={FileText} boxSize={4.5} color="gray.500" mt={1} />
              <Text whiteSpace="pre-wrap" color="gray.700">
                {task.description || 'No description provided.'}
              </Text>
            </HStack>
          </TaskSectionCard>

          <TaskSectionCard
            title="Working notes"
            description="Internal remarks, blockers, or handoff detail."
          >
            <HStack align="flex-start" spacing={3}>
              <Icon as={NotebookPen} boxSize={4.5} color="gray.500" mt={1} />
              <Text whiteSpace="pre-wrap" color="gray.700">
                {task.remarks || 'No remarks yet.'}
              </Text>
            </HStack>
          </TaskSectionCard>
        </Stack>

        <VStack align="stretch" spacing={6}>
          <TaskSectionCard title="State" description="Status, priority, and urgency.">
            <Stack spacing={4}>
              <HStack justify="space-between">
                <Text color="gray.600">Status</Text>
                <TaskStatusBadge status={task.status} />
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.600">Priority</Text>
                <TaskPriorityBadge priority={task.priority} />
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.600">Urgency</Text>
                <Text fontWeight="800" color={getTaskUrgencyColor(task)}>
                  {getTaskUrgencyLabel(task)}
                </Text>
              </HStack>
            </Stack>
          </TaskSectionCard>

          <TaskSectionCard title="Timeline" description="Important timestamps for this task.">
            <Stack spacing={4}>
              <HStack align="flex-start" spacing={3}>
                <Icon as={Clock3} boxSize={4.5} color="gray.500" mt={1} />
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Start date
                  </Text>
                  <Text color="gray.900">{formatTaskDate(task.start_date)}</Text>
                </Box>
              </HStack>
              <HStack align="flex-start" spacing={3}>
                <Icon as={Clock3} boxSize={4.5} color="gray.500" mt={1} />
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Due date
                  </Text>
                  <Text color="gray.900">{formatTaskDate(task.due_date)}</Text>
                </Box>
              </HStack>
              <HStack align="flex-start" spacing={3}>
                <Icon as={UserRound} boxSize={4.5} color="gray.500" mt={1} />
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Completed at
                  </Text>
                  <Text color="gray.900">{formatTaskDateTime(task.completed_at)}</Text>
                </Box>
              </HStack>
            </Stack>
          </TaskSectionCard>

          <TaskSectionCard title="Actions" description="Move the task forward or remove it.">
            <Stack spacing={3}>
              {!isTaskComplete(task) ? (
                <Button
                  leftIcon={<Icon as={CheckCircle2} />}
                  borderRadius="16px"
                  colorScheme="green"
                  bg="#166534"
                  _hover={{ bg: '#14532D' }}
                  onClick={handleComplete}
                  isLoading={processing}
                >
                  Mark complete
                </Button>
              ) : null}
              <Button
                leftIcon={<DeleteIcon />}
                borderRadius="16px"
                variant="outline"
                colorScheme="red"
                onClick={onOpen}
              >
                Delete task
              </Button>
            </Stack>
          </TaskSectionCard>
        </VStack>
      </SimpleGrid>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={processing}
        AreYouSureQuestion="Delete task?"
        AreYouSureTextExplanation={`This will permanently remove ${task.title}.`}
        confirmButtonText="Delete task"
        confirmButtonColor="#DC2626"
        colorScheme="red"
      />
    </TaskShell>
  );
}
